/**
 * Membership-plan access-hours API wrapper (DNKADM-0315, wiring DNKAPI-0457).
 *
 * A membership plan may carry an OPTIONAL weekly access-hours cap: the window in
 * which members on that plan may book. The booking engine resolves a configured cap
 * as `intersect(physicalShifts, configuredShifts(date))` — it NARROWS when a member can
 * book against the real bay/staff schedules and can never widen beyond them. An
 * unconfigured plan keeps the full member window (the pre-0457 behavior).
 *
 * **Endpoints** — under the owned plan, bearer-only `/v2/admin/**` surface (`request()`
 * routes it, DNKADM-0049 `isV2Surface`: bearer token, NO CSRF, NO cookies, the bounded
 * 401→refresh→retry; the mutating-audit `X-Session-Mode` stamp fires on the writes):
 *  - `GET    /v2/admin/membership-plans/{id}/access-hours` → {@link PlanAccessHours}.
 *  - `PUT    /v2/admin/membership-plans/{id}/access-hours` ← `{ days: [...] }` (ALWAYS all
 *    seven weekday rows — an all-closed configured schedule is legitimate and stays
 *    distinct from unconfigured).
 *  - `DELETE /v2/admin/membership-plans/{id}/access-hours` → 204; the plan returns to
 *    unconfigured (full member window).
 *
 * **Wire shape:** the location-hours DTOs verbatim (`WeeklyHoursRequest` /
 * `DayHoursDto` / `ShiftDto`): `dayOfWeek` 0=Sunday … 6=Saturday, the wire field is
 * `isAvailable` (the DB column is `is_open` — never renamed on the client), shifts are
 * `startTime`/`endTime` `"HH:mm"`. The types and the parse/serialize helpers are
 * IMPORTED from `operatingHours.ts`, not redefined, so a day round-trips identically
 * to the location editor.
 *
 * **`days === null` means unconfigured.** That is the one semantic this module adds
 * over the location client: the response's `days` is nullable, and the UI must render
 * an explicit "not configured" state for it — never an empty seven-day grid, which
 * reads as "closed all week".
 */
import { request } from './client';
import { dayToBody, parseDays, type WeeklyHoursDay } from './operatingHours';

/**
 * Parsed `GET …/access-hours`. `membershipPlanId` echoes the plan (a `Long` on the wire,
 * normalized to the string id the UI uses; `null` if the body carries none). `days` is
 * `null` when the plan is UNCONFIGURED (full member window), else always the seven
 * days in `dayOfWeek` 0→6 order.
 */
export interface PlanAccessHours {
  membershipPlanId: string | null;
  days: WeeklyHoursDay[] | null;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** The plan id echo — a finite number → `String(n)`, a non-empty string → trimmed, else null. */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function parsePlanAccessHours(raw: unknown): PlanAccessHours {
  const o = isObject(raw) ? raw : {};
  const days = o['days'];
  return {
    membershipPlanId: asId(o['membershipPlanId']),
    // `null`/absent ⇒ unconfigured. Anything else normalizes to the seven days (a
    // configured schedule always carries all seven on the wire; the fill is defensive).
    days: days === null || days === undefined ? null : parseDays(days),
  };
}

/** v2 admin membership-plans base path (bearer surface). */
const PLANS_PATH = '/v2/admin/membership-plans';

/** The `…/access-hours` path for a plan id. */
function accessHoursPath(membershipPlanId: string): string {
  return `${PLANS_PATH}/${encodeURIComponent(membershipPlanId)}/access-hours`;
}

/**
 * `GET /v2/admin/membership-plans/{id}/access-hours` → the plan's optional weekly cap.
 * `days` is `null` when unconfigured. Throws a typed `ApiError` on a non-2xx response
 * (`404` NOT_FOUND for an unknown/deleted plan).
 */
export async function getPlanAccessHours(membershipPlanId: string): Promise<PlanAccessHours> {
  const body = await request<unknown>('GET', accessHoursPath(membershipPlanId));
  return parsePlanAccessHours(body);
}

/**
 * `PUT /v2/admin/membership-plans/{id}/access-hours` — replace the plan's weekly cap
 * with ALL seven days (an unavailable day with `shifts: []`, an available day with its
 * 1..n shifts). Client validation should run first (`validateHours.ts`, the same rules
 * as the location editor). Throws a typed `ApiError`: `400` INVALID_REQUEST /
 * VALIDATION (bad shift / overlap / duplicate day), `403`, `404`. Refetch after this
 * resolves.
 */
export async function putPlanAccessHours(
  days: WeeklyHoursDay[],
  membershipPlanId: string,
): Promise<void> {
  await request<unknown>('PUT', accessHoursPath(membershipPlanId), {
    json: { days: days.map(dayToBody) },
  });
}

/**
 * `DELETE /v2/admin/membership-plans/{id}/access-hours` — unset the cap (204). The plan
 * returns to UNCONFIGURED, i.e. the full member window — not all-closed. Idempotent on
 * the server (deleting an unconfigured plan's cap is a no-op 204). Refetch after this
 * resolves.
 */
export async function deletePlanAccessHours(membershipPlanId: string): Promise<void> {
  await request<unknown>('DELETE', accessHoursPath(membershipPlanId));
}
